import { CAMPAIGN_LEVELS } from '../../content/levels/campaign';
import { allowedMowers, type LevelDef } from '../../content/levelSchema';
import { isMowerId, MOWER_DEFS, type MowerDef, type MowerId } from '../../content/mowers';
import { simTool, TOOL_BY_ID, type ToolId } from '../../content/tools';
import {
  dailyLawn,
  ENDLESS_MODES,
  endlessCarry,
  endlessModeAllows,
  endlessRunLawn,
  endlessStagePassed,
  endlessStageScore,
  RACE_SIZES,
  raceLawn,
  type EndlessMode,
  type RaceSize,
} from '../../gen/modes';
import type { Intent } from '../../sim/mower';
import { decodeIntent, encodeIntent, playReplay } from '../../sim/replay';
import type { CarryOver, LoggedIntent, Simulation, SimStatus } from '../../sim/simulation';
import { UPGRADES, upgradeCosts, upgradedMower, upgradedTool, type UpgradeKind } from '../economy';
import type { TileId } from '../../content/tiles';
import { rulesHash } from '../rulesHash';
import { lawnHash } from './boards';
import { communityLevel } from './communityLawn';
import { dailyHasBoxes, RULES_CHECKED, simRulesFor } from '../rulesVersion';
import { UpgradeLevelsSchema, type UpgradeLevels } from '../save/schema';

/**
 * A run as the game posts it (docs/ONLINE.md, "What a run sends"): which board, the mower and its
 * loadout, and each lawn's inputs. Never the lawn or the mower's stats: {@link checkRun} rebuilds
 * both from the rules, so a tampered lawn or a faster mower cannot be posted.
 */
export interface PostedRun {
  /** The rules version it was played on (RULES_VERSION). */
  rules: number;
  board: RunBoard;
  mower: MowerId;
  /** Upgrades and tools (campaign and Endless; the Daily's loaner has none). */
  loadout?: RunLoadout;
  /**
   * The paint the mower wore (a cosmetic, nothing more): its ghost wears it too. Never checked, and
   * an unknown one is shown as the mower's own colors.
   */
  paint?: string;
  /** One per lawn played: one for the Daily and a campaign lawn, the run's lawns for Endless. */
  lawns: PostedLawn[];
  /** What the game says happened; checking the run must reach exactly this. */
  claimed: RunResult;
}

export type RunBoard =
  | { kind: 'daily'; date: string }
  /** A campaign lawn by number (1-based). */
  | { kind: 'campaign'; lawn: number }
  /** `firstLawnFor`: the mower the first lawn was built for (absent: Endless Lawns from the menu). */
  | { kind: 'endless'; mode: EndlessMode; seed: string; firstLawnFor?: MowerId }
  /** A live race's lawn (docs/LIVE.md): the room's seed and the size the inviter picked. */
  | { kind: 'race'; seed: string; size: RaceSize }
  /**
   * A community lawn (docs/ONLINE.md): its id, and its code, which the run check rebuilds the lawn
   * from (the server also checks the code is that lawn's).
   */
  | { kind: 'community'; id: string; code: string };

export interface RunLoadout {
  upgrades?: Partial<UpgradeLevels>;
  /** In slot order: tool and its upgrade level. */
  tools?: [ToolId, number][];
}

export interface PostedLawn {
  /** The tick the lawn's recording stopped at. */
  endTick: number;
  /** `[tick, intent code]` pairs (encodeIntent), in tick order. */
  inputs: [number, number][];
}

export interface RunResult {
  /** Ticks played, over every lawn. */
  ticks: number;
  /** Endless: the run's score. The Daily and campaign lawns: 0 (they rank by ticks). */
  score: number;
  /** How the last lawn ended (`running`: the player stopped the run partway through it). */
  status: SimStatus;
}

export type CheckResult = CheckedRun | { ok: false; problem: string };

export interface CheckedRun extends RunResult {
  ok: true;
  /** A campaign lawn finished with a paid Continue. */
  revived: boolean;
  /**
   * A hash of the first lawn and the stock mower: runs with the same key played the same lawn
   * under the same rules, so they can share a board (docs/ONLINE.md, "Boards").
   */
  lawnKey: string;
  /** The first lawn's key without the mower (the featured board's scope, `lawnHash`). */
  lawnHash: string;
  /** Signs a person may not have played it, for the owner to look at (never a refusal). */
  flags: RunFlag[];
  /**
   * A campaign soccer lawn (played to the whistle): the goals scored and the grass mowed, in
   * tenths of a percent, which rank it instead of its time.
   */
  soccer?: { goals: number; mowedPermille: number };
  /**
   * A race run: the grass it left (two uncleared lawns go to the less left) and the tick each of
   * the lawn's coins was picked up, in the level's order (-1: never), for settling who took it.
   */
  race?: { grassLeft: number; coinTicks: number[] };
}

/**
 * `machine-timing`: nearly every turn entered on the very tick the mower reached a tile center,
 * the way the autopilot steers; a thumb lands anywhere along the way.
 */
export type RunFlag = 'machine-timing';

/** Turns needed before timing says anything, and the share at tile centers that flags a run. */
export const MACHINE_TIMING_MIN_TURNS = 20;
export const MACHINE_TIMING_SHARE = 0.6;

/** Half an hour a lawn: more than any real lawn takes, and a cap on the work a check can cost. */
export const MAX_LAWN_TICKS = 30 * 60 * 60;
export const MAX_LAWN_INPUTS = 5000;
export const MAX_RUN_LAWNS = 200;

const REVIVE = 50;
const isBoost = (code: number) => code >= 60 && code < 70;
const isTool = (code: number) => code >= 40 && code < 50;
const isItem = (code: number) => code >= 80 && code < 90;

/** One lawn's part of a run, from the sim that played it. */
export function postedLawn(sim: Simulation): PostedLawn {
  return {
    endTick: sim.tick,
    inputs: sim.intentLog.map(({ tick, intent }) => [tick, encodeIntent(intent)]),
  };
}

/** What a board lets a run use, and the lawn each stage plays. */
interface BoardPlan {
  lawn: (stage: number) => LevelDef;
  spec: MowerDef;
  maxLawns: number;
  allowTools: boolean;
  allowBoosts: boolean;
  /** Item boxes' inputs (the Daily from rules version 5, docs/DAILY_ITEMS.md). */
  allowItems: boolean;
  allowRevive: boolean;
  endless: EndlessMode | null;
  race: boolean;
}

const NO_UPGRADES = UpgradeLevelsSchema.parse({});

function planFor(run: PostedRun): BoardPlan | string {
  const { board, mower } = run;
  if (!isMowerId(mower)) return `no mower ${String(mower)}`;
  if (board.kind === 'daily') {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(board.date)) return 'bad date';
    const boxes = dailyHasBoxes(run.rules);
    const daily = dailyLawn(board.date, { boxes });
    if (mower !== daily.loaner) return `the Daily is played with the ${daily.loaner}`;
    if (run.loadout) return 'the Daily is played without upgrades or tools';
    const lawn = daily.def;
    return {
      lawn: () => lawn,
      spec: MOWER_DEFS[mower],
      maxLawns: 1,
      allowTools: false,
      allowBoosts: false,
      allowItems: boxes,
      allowRevive: false,
      endless: null,
      race: false,
    };
  }
  if (board.kind === 'race') {
    if (typeof board.seed !== 'string' || board.seed.length === 0 || board.seed.length > 64) {
      return 'bad race seed';
    }
    if (!RACE_SIZES.includes(board.size)) return `no race size ${String(board.size)}`;
    const race = raceLawn(board.seed, board.size);
    if (mower !== race.loaner) return `this race is mowed with the ${race.loaner}`;
    if (run.loadout) return 'a race is mowed without upgrades or tools';
    const lawn = race.def;
    return {
      lawn: () => lawn,
      spec: MOWER_DEFS[mower],
      maxLawns: 1,
      allowTools: false,
      allowBoosts: false,
      allowItems: false,
      allowRevive: false,
      endless: null,
      race: true,
    };
  }
  const loadout = checkLoadout(mower, run.loadout);
  if (typeof loadout === 'string') return loadout;
  if (board.kind === 'community') {
    if (typeof board.id !== 'string' || !/^[0-9a-z]{10}$/.test(board.id)) return 'bad lawn id';
    const lawn = typeof board.code === 'string' ? communityLevel(board.code) : null;
    if (!lawn) return 'not a lawn this game reads';
    if (!allowedMowers(lawn.mowerRule).includes(mower)) return `${mower} not allowed on this lawn`;
    return {
      lawn: () => lawn,
      spec: loadout,
      maxLawns: 1,
      allowTools: true,
      allowBoosts: true,
      allowItems: false,
      allowRevive: false,
      endless: null,
      race: false,
    };
  }
  if (board.kind === 'campaign') {
    const lawn = CAMPAIGN_LEVELS[board.lawn - 1];
    if (!Number.isInteger(board.lawn) || !lawn) return `no campaign lawn ${board.lawn}`;
    if (!allowedMowers(lawn.mowerRule).includes(mower)) return `${mower} not allowed on ${lawn.id}`;
    return {
      lawn: () => lawn,
      spec: loadout,
      maxLawns: 1,
      allowTools: true,
      allowBoosts: true,
      allowItems: false,
      allowRevive: true,
      endless: null,
      race: false,
    };
  }
  const { mode, seed, firstLawnFor } = board;
  if (!ENDLESS_MODES.some((m) => m.id === mode)) return `no Endless mode ${String(mode)}`;
  if (typeof seed !== 'string' || seed.length === 0 || seed.length > 64) return 'bad run seed';
  if (!endlessModeAllows(mode, mower)) return `${mower} cannot play ${mode}`;
  if (firstLawnFor !== undefined && !isMowerId(firstLawnFor)) return 'bad first lawn mower';
  if (firstLawnFor === undefined && mode !== 'lawns') return `${mode} lawns are built for a mower`;
  if (firstLawnFor !== undefined && !endlessModeAllows(mode, firstLawnFor)) {
    return `${mode} lawns are not built for ${firstLawnFor}`;
  }
  return {
    lawn: (stage) => endlessRunLawn(mode, seed, stage, stage === 1 ? firstLawnFor : mower),
    spec: loadout,
    maxLawns: MAX_RUN_LAWNS,
    allowTools: true,
    allowBoosts: true,
    allowItems: false,
    allowRevive: false,
    endless: mode,
    race: false,
  };
}

/** The mower as upgraded, if the upgrades are ones the game sells for it. */
function checkLoadout(mower: MowerId, loadout: RunLoadout | undefined): MowerDef | string {
  const ups = loadout?.upgrades ?? {};
  for (const [kind, level] of Object.entries(ups)) {
    if (kind === 'engineInUse') continue;
    if (!Object.hasOwn(UPGRADES, kind)) return `no upgrade ${kind}`;
    const max = upgradeCosts(mower, kind as UpgradeKind).length;
    if (!Number.isInteger(level) || level < 0 || level > max) return `${kind} ${level} on ${mower}`;
  }
  const levels = { ...NO_UPGRADES, ...ups };
  if (levels.engineInUse !== undefined) {
    const n = levels.engineInUse;
    if (!Number.isInteger(n) || n < 0 || n > levels.engine) return 'engine dialed past its level';
  }
  const tools = loadout?.tools ?? [];
  if (tools.length > MOWER_DEFS[mower].toolSlots) return `${mower} carries fewer tools`;
  const seen = new Set<string>();
  for (const [id, level] of tools) {
    const def = Object.hasOwn(TOOL_BY_ID, id) ? TOOL_BY_ID[id] : undefined;
    if (!def?.available) return `no tool ${String(id)}`;
    if (seen.has(id)) return `${id} twice`;
    seen.add(id);
    if (!Number.isInteger(level) || level < 0 || level > def.upgrades.length) {
      return `${id} level ${level}`;
    }
  }
  return upgradedMower(MOWER_DEFS[mower], levels);
}

/**
 * The mower (with its upgrades) and tools a posted run was played with, to replay it as a ghost on
 * `art`'s lawn; null when the run's loadout is not one the game sells.
 */
export function runGear(
  run: Pick<PostedRun, 'mower' | 'loadout'>,
  art: readonly TileId[],
): { mower: MowerDef; tools: ReturnType<typeof simTool>[] } | null {
  if (!isMowerId(run.mower)) return null;
  const mower = checkLoadout(run.mower, run.loadout);
  if (typeof mower === 'string') return null;
  const tools = (run.loadout?.tools ?? []).map(([id, lvl]) =>
    simTool(upgradedTool(TOOL_BY_ID[id], lvl), art),
  );
  return { mower, tools };
}

/** A lawn's inputs as intents, if they are well formed and the board allows them. */
function readInputs(lawn: PostedLawn, plan: BoardPlan, tools: number): LoggedIntent[] | string {
  if (!Number.isInteger(lawn.endTick) || lawn.endTick < 0 || lawn.endTick > MAX_LAWN_TICKS) {
    return 'bad end tick';
  }
  if (!Array.isArray(lawn.inputs) || lawn.inputs.length > MAX_LAWN_INPUTS) return 'too many inputs';
  const intents: LoggedIntent[] = [];
  let last = 0;
  for (const pair of lawn.inputs) {
    const [tick, code] = pair;
    if (!Number.isInteger(tick) || tick < last) return 'inputs out of order';
    if (tick > lawn.endTick + 1) return 'an input after the lawn ended';
    last = tick;
    if (code === REVIVE && !plan.allowRevive) return 'no continues here';
    if (isBoost(code) && !plan.allowBoosts) return 'no boosts here';
    if (isItem(code) && !plan.allowItems) return 'no items here';
    if (isTool(code) && (!plan.allowTools || code - 40 >= tools)) return 'no such tool slot';
    let intent: Intent;
    try {
      intent = decodeIntent(code);
    } catch {
      return `unknown input ${String(code)}`;
    }
    intents.push({ tick, intent });
  }
  return intents;
}

/**
 * Checks a posted run the way the server does: rebuilds each lawn and the mower from the rules,
 * replays the inputs, scores the result and compares it with the claim. Pure and deterministic,
 * so the game and the server always agree.
 */
export function checkRun(run: PostedRun): CheckResult {
  if (!RULES_CHECKED.includes(run.rules)) return { ok: false, problem: `rules ${run.rules}` };
  const plan = planFor(run);
  if (typeof plan === 'string') return { ok: false, problem: plan };
  if (!Array.isArray(run.lawns) || run.lawns.length === 0 || run.lawns.length > plan.maxLawns) {
    return { ok: false, problem: 'wrong number of lawns' };
  }
  let ticks = 0;
  let score = 0;
  let status: SimStatus = 'running';
  let revived = false;
  let carry: CarryOver = {};
  let turns = 0;
  let turnsAtCenter = 0;
  let lawnKey = '';
  let lawnOnly = '';
  let race: CheckedRun['race'];
  let soccer: CheckedRun['soccer'];
  const countTurn = (sim: Readonly<Simulation>, intent: Intent) => {
    if (intent.type !== 'face' && intent.type !== 'turn') return;
    turns++;
    if (sim.mower.progress === 0 && sim.tick > 0) turnsAtCenter++;
  };
  for (const [i, lawn] of run.lawns.entries()) {
    const stage = i + 1;
    const level = plan.lawn(stage);
    const toolSpecs = plan.allowTools
      ? (run.loadout?.tools ?? []).map(([id, lvl]) =>
          simTool(upgradedTool(TOOL_BY_ID[id], lvl), level.art),
        )
      : [];
    const intents = readInputs(lawn, plan, toolSpecs.length);
    if (typeof intents === 'string') return { ok: false, problem: `lawn ${stage}: ${intents}` };
    if (stage === 1) {
      lawnKey = rulesHash({ lawn: level, mower: MOWER_DEFS[run.mower] });
      lawnOnly = lawnHash(level);
    }
    // A race notes the tick each coin was picked up.
    const coins = plan.race
      ? (level.sim.pickups ?? []).flatMap((p, index) => (p.kind === 'coin' ? [index] : []))
      : [];
    const coinTicks = coins.map(() => -1);
    const noteCoins = (sim: Readonly<Simulation>) =>
      coins.forEach((index, i) => {
        if (coinTicks[i] === -1 && sim.isCollected(index)) coinTicks[i] = sim.tick;
      });
    const sim = playReplay(
      {
        version: 1,
        level: level.sim,
        mower: plan.spec,
        tools: toolSpecs,
        carry,
        intents,
        endTick: lawn.endTick,
        // By the sim's rules on the run's version (a run from before version 6, by those).
        rules: simRulesFor(run.rules),
      },
      countTurn,
      plan.race ? noteCoins : undefined,
    );
    if (sim.tick !== lawn.endTick) {
      return { ok: false, problem: `lawn ${stage}: ended at ${sim.tick}, not ${lawn.endTick}` };
    }
    ticks += sim.tick;
    status = sim.status;
    if (plan.race) race = { grassLeft: sim.grid.grassRemaining, coinTicks };
    if (run.board.kind === 'campaign' && level.sim.soccer) {
      const { totalGrass, grassRemaining } = sim.grid;
      soccer = {
        goals: sim.goals,
        mowedPermille: Math.floor(((totalGrass - grassRemaining) * 1000) / Math.max(1, totalGrass)),
      };
    }
    revived ||= intents.some(({ intent }) => intent.type === 'revive');
    const last = stage === run.lawns.length;
    if (plan.endless) {
      const passed = endlessStagePassed(plan.endless, stage, sim);
      if (!last && !passed) return { ok: false, problem: `lawn ${stage} ended the run` };
      // A lawn stopped partway through (ended from pause) counts nothing.
      if (sim.status !== 'running')
        score += endlessStageScore(plan.endless, sim, sim.status === 'won');
      carry = endlessCarry(sim);
    }
  }
  const result: RunResult = { ticks, score, status };
  const claimed = run.claimed;
  if (claimed.ticks !== ticks || claimed.score !== score || claimed.status !== status) {
    return {
      ok: false,
      problem: `claimed ${JSON.stringify(claimed)}, played ${JSON.stringify(result)}`,
    };
  }
  const flags: RunFlag[] = [];
  if (turns >= MACHINE_TIMING_MIN_TURNS && turnsAtCenter / turns >= MACHINE_TIMING_SHARE) {
    flags.push('machine-timing');
  }
  return {
    ok: true,
    ...result,
    revived,
    lawnKey,
    lawnHash: lawnOnly,
    flags,
    ...(race ? { race } : {}),
    ...(soccer ? { soccer } : {}),
  };
}
